import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Filter, Grid3X3, LayoutGrid, Search, SlidersHorizontal } from 'lucide-react';
import { demoNfts, demoMarketplace } from '@/services/demoData';
import { motion } from 'framer-motion';

interface NFTCardProps {
  id: number;
  view: 'grid' | 'list';
  onBuy: () => void;
  nft: any;
}

const NFTCard: React.FC<NFTCardProps> = ({ id, view, onBuy, nft }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: id * 0.1 }}
    >
      <Card className={`overflow-hidden shadow-3d hover:shadow-3d-hover transition-all duration-300 ${view === 'list' ? 'flex' : ''}`}>
        <div className={`${view === 'grid' ? 'aspect-square' : 'w-40 h-40'} bg-muted relative group`}>
          <img 
            src={nft.image}
            alt={nft.name} 
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <Link 
            to={`/nft/${nft.id}`} 
            className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center"
          >
            <Button 
              variant="secondary" 
              size="sm"
              className="shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
            >
              View Details
            </Button>
          </Link>
        </div>
        <div className={`${view === 'list' ? 'flex-1' : ''}`}>
          <CardContent className="p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-medium text-lg text-shadow">{nft.name}</h3>
                <p className="text-sm text-muted-foreground">{nft.creatorName}</p>
              </div>
              {view === 'list' && (
                <div className="text-right">
                  <p className="font-medium text-lg text-shadow">{nft.price} DUSD</p>
                  <p className="text-xs text-muted-foreground">≈ ${parseFloat(nft.price) * 1.2}</p>
                </div>
              )}
            </div>
            {view === 'grid' && (
              <div className="flex justify-between items-center mt-2">
                <p className="font-medium text-lg text-shadow">{nft.price} DUSD</p>
                <p className="text-xs text-muted-foreground">≈ ${parseFloat(nft.price) * 1.2}</p>
              </div>
            )}
            {view === 'list' && (
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {nft.description}
              </p>
            )}
          </CardContent>
          <CardFooter className="px-4 py-3 border-t">
            {nft.forSale ? (
              <Button 
                className="w-full shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                onClick={onBuy}
              >
                Buy Now
              </Button>
            ) : (
              <Button 
                className="w-full shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
                variant="outline"
              >
                Make Offer
              </Button>
            )}
          </CardFooter>
        </div>
      </Card>
    </motion.div>
  );
};

const MarketplacePage = () => {
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [activeCategory, setActiveCategory] = useState('all');
  const { isAuthenticated } = useAuthStore();

  // Handle buying an NFT
  const handleBuyNFT = async (nftId: string) => {
    if (!isAuthenticated) {
      toast.error('Please connect your wallet to buy NFTs');
      return;
    }

    try {
      toast.loading('Processing purchase...', { id: 'buy-nft' });
      
      // Simulate purchase delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('NFT purchased successfully!', { id: 'buy-nft' });
    } catch (error) {
      console.error('Error buying NFT:', error);
      toast.error('Failed to purchase NFT', { id: 'buy-nft' });
    }
  };

  // Filter NFTs by category
  const filteredNfts = activeCategory === 'all' 
    ? demoNfts 
    : demoNfts.filter(nft => {
        if (activeCategory === 'forSale') return nft.forSale;
        // Add more filters as needed
        return true;
      });

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8"
      >
        <div>
          <h1 className="text-3xl font-bold text-shadow">NFT Marketplace</h1>
          <p className="text-muted-foreground">
            Discover and collect unique digital assets from creators around the world
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Button 
            asChild
            className="shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
          >
            <Link to="/dashboard/nfts/mint">Create NFT</Link>
          </Button>
        </div>
      </motion.div>
      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Filters Sidebar */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full md:w-64 flex-shrink-0"
        >
          <Card className="shadow-3d hover:shadow-3d-hover transition-all duration-300">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-shadow">Filters</h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 px-2 hover:shadow-3d-hover transition-all duration-300"
                >
                  <Filter className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2 text-shadow">Status</h4>
                  <div className="space-y-2">
                    <label className="flex items-center hover:scale-105 transition-transform duration-300">
                      <input 
                        type="checkbox" 
                        className="mr-2 accent-primary" 
                        defaultChecked 
                        onChange={() => setActiveCategory(activeCategory === 'forSale' ? 'all' : 'forSale')}
                      />
                      <span className="text-sm">Buy Now</span>
                    </label>
                    <label className="flex items-center hover:scale-105 transition-transform duration-300">
                      <input type="checkbox" className="mr-2 accent-primary" />
                      <span className="text-sm">On Auction</span>
                    </label>
                    <label className="flex items-center hover:scale-105 transition-transform duration-300">
                      <input type="checkbox" className="mr-2 accent-primary" />
                      <span className="text-sm">New</span>
                    </label>
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-medium mb-2 text-shadow">Price</h4>
                  <div className="flex items-center">
                    <input 
                      type="number" 
                      placeholder="Min" 
                      className="w-full p-2 text-sm border rounded-md mr-2 shadow-3d focus:shadow-3d-hover transition-all duration-300"
                    />
                    <span>to</span>
                    <input 
                      type="number" 
                      placeholder="Max" 
                      className="w-full p-2 text-sm border rounded-md ml-2 shadow-3d focus:shadow-3d-hover transition-all duration-300"
                    />
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <h4 className="text-sm font-medium mb-2 text-shadow">Collections</h4>
                  <div className="space-y-2">
                    <label className="flex items-center hover:scale-105 transition-transform duration-300">
                      <input type="checkbox" className="mr-2 accent-primary" />
                      <span className="text-sm">Digital Art Collection</span>
                    </label>
                    <label className="flex items-center hover:scale-105 transition-transform duration-300">
                      <input type="checkbox" className="mr-2 accent-primary" />
                      <span className="text-sm">Metaverse Landscapes</span>
                    </label>
                    <label className="flex items-center hover:scale-105 transition-transform duration-300">
                      <input type="checkbox" className="mr-2 accent-primary" />
                      <span className="text-sm">Cosmic Voyagers</span>
                    </label>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="px-4 py-3 border-t">
              <Button 
                className="w-full shadow-3d hover:shadow-3d-hover transform hover:scale-105 transition-all duration-300"
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
        
        {/* Main Content */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex-1"
        >
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input 
                  type="text" 
                  placeholder="Search NFTs..." 
                  className="w-full pl-10 pr-4 py-2 border rounded-md shadow-3d focus:shadow-3d-hover transition-all duration-300"
                />
              </div>
              <div className="flex items-center">
                <span className="text-sm text-muted-foreground mr-2">Sort by:</span>
                <select className="p-2 border rounded-md shadow-3d focus:shadow-3d-hover transition-all duration-300">
                  <option>Recently Listed</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Most Viewed</option>
                </select>
              </div>
              <div className="flex items-center border rounded-md overflow-hidden shadow-3d">
                <button 
                  className={`p-2 transition-all duration-300 ${view === 'grid' ? 'bg-muted shadow-3d-hover' : 'hover:bg-muted/50'}`}
                  onClick={() => setView('grid')}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button 
                  className={`p-2 transition-all duration-300 ${view === 'list' ? 'bg-muted shadow-3d-hover' : 'hover:bg-muted/50'}`}
                  onClick={() => setView('list')}
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
          
          <Tabs defaultValue="all">
            <TabsList className="mb-6 shadow-3d">
              <TabsTrigger value="all" className="hover:shadow-3d-hover transition-all duration-300">All</TabsTrigger>
              <TabsTrigger value="art" className="hover:shadow-3d-hover transition-all duration-300">Art</TabsTrigger>
              <TabsTrigger value="collectibles" className="hover:shadow-3d-hover transition-all duration-300">Collectibles</TabsTrigger>
              <TabsTrigger value="photography" className="hover:shadow-3d-hover transition-all duration-300">Photography</TabsTrigger>
              <TabsTrigger value="music" className="hover:shadow-3d-hover transition-all duration-300">Music</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : ''} gap-6`}>
                {filteredNfts.map((nft, index) => (
                  <NFTCard
                    key={nft.id}
                    id={index}
                    view={view}
                    onBuy={() => handleBuyNFT(nft.id)}
                    nft={nft}
                  />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="art">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : ''} gap-6`}>
                {filteredNfts.filter((_, i) => i % 3 === 0).map((nft, index) => (
                  <NFTCard
                    key={nft.id}
                    id={index}
                    view={view}
                    onBuy={() => handleBuyNFT(nft.id)}
                    nft={nft}
                  />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="collectibles">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : ''} gap-6`}>
                {filteredNfts.filter((_, i) => i % 3 === 1).map((nft, index) => (
                  <NFTCard
                    key={nft.id}
                    id={index}
                    view={view}
                    onBuy={() => handleBuyNFT(nft.id)}
                    nft={nft}
                  />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="photography">
              <div className={`grid ${view === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : ''} gap-6`}>
                {filteredNfts.filter((_, i) => i % 3 === 2).map((nft, index) => (
                  <NFTCard
                    key={nft.id}
                    id={index}
                    view={view}
                    onBuy={() => handleBuyNFT(nft.id)}
                    nft={nft}
                  />
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="music">
              <div className="text-center py-12">
                <p className="text-gray-400">No music NFTs found</p>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="mt-8 flex justify-center">
            <Button variant="outline">Load More</Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MarketplacePage;